import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ArrowDownUp, Landmark, Lock, Plus, Trash2, Wallet as WalletIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SampleBanner } from "@/components/SampleBanner";
import {
    createDemoSponsorWallet, createMarketWallet, fetchMarketSponsors, fetchMarketWallet,
    topUpMarketWallet,
} from "@/lib/api";
import { money } from "@/lib/format";
import { clearWallet, readWallet, saveWallet, timeLeft } from "@/lib/market";

const NONE = "__none__";
const PRESETS = [100, 250, 500, 1000];
const KIND_META = {
    topup: { label: "Top-up", tone: "text-brand" },
    hold: { label: "Hold", tone: "text-heat" },
    hold_release: { label: "Hold released", tone: "text-brand" },
    capture: { label: "Captured", tone: "text-heat" },
    refund: { label: "Refund", tone: "text-brand" },
};

/** Balance, holds and top-ups for the device-local marketplace wallet. */
export default function Wallet() {
    const queryClient = useQueryClient();
    const [wallet, setWallet] = useState(() => readWallet());
    const [label, setLabel] = useState("");
    const [sponsorId, setSponsorId] = useState(NONE);
    const [creating, setCreating] = useState(false);
    const [custom, setCustom] = useState("");
    const [busy, setBusy] = useState(false);

    const sponsors = useQuery({ queryKey: ["market-sponsors"], queryFn: fetchMarketSponsors });
    const snap = useQuery({
        queryKey: ["market-wallet", wallet?.id],
        queryFn: () => fetchMarketWallet(wallet.id, wallet),
        enabled: !!wallet,
        retry: false,
        refetchInterval: 20000,
    });

    const forget = () => {
        clearWallet();
        setWallet(null);
        toast.success("Wallet removed from this device");
    };

    const createOwn = async () => {
        const name = label.trim();
        if (name.length < 2 || creating) {
            toast.error("Give the wallet a name (2–80 characters)");
            return;
        }
        setCreating(true);
        try {
            const data = await createMarketWallet(name);
            saveWallet({
                id: data.wallet.id,
                token: data.token,
                label: data.wallet.label,
                demo: !!data.wallet.is_demo,
            });
            setWallet(readWallet());
            toast.success("Wallet created — the access token stays on this device");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create the wallet");
        } finally {
            setCreating(false);
        }
    };

    const useSponsor = async () => {
        if (sponsorId === NONE || creating) return;
        setCreating(true);
        try {
            const data = await createDemoSponsorWallet(sponsorId);
            saveWallet({
                id: data.wallet.id,
                token: null,
                label: data.wallet.label,
                demo: true,
            });
            setWallet(readWallet());
            toast.success(`Bidding as ${data.sponsor.brand} — seeded demo funds`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open that sponsor wallet");
        } finally {
            setCreating(false);
        }
    };

    const topUp = async (amountUsd) => {
        const value = Math.trunc(Number(amountUsd));
        if (!Number.isFinite(value) || value <= 0 || busy || !wallet) return;
        setBusy(true);
        try {
            await topUpMarketWallet(wallet.id, value, wallet);
            queryClient.invalidateQueries({ queryKey: ["market-wallet", wallet.id] });
            toast.success(`Topped up ${money(value)} — demo credits, no card charged`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Top-up failed");
        } finally {
            setBusy(false);
        }
    };

    if (!wallet) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
                <SampleBanner className="mb-6" />
                <h1 className="font-heading text-3xl font-bold tracking-tight inline-flex items-center gap-3">
                    <WalletIcon className="h-7 w-7 text-brand" /> Open a wallet
                </h1>
                <p className="text-muted-foreground mt-2 mb-6 text-sm sm:text-base">
                    Bids hold funds from your wallet. No account, no card — the wallet lives on this
                    device and demo credits carry no real money.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <Card className="bg-card border-border p-5">
                        <h2 className="font-heading font-semibold mb-1">Create your wallet</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            For sponsors bidding their own budget. You get a device-local access token.
                        </p>
                        <Label htmlFor="wallet-label">Wallet name</Label>
                        <Input
                            id="wallet-label"
                            data-testid="wallet-label-input"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. NorthPeak Nutrition"
                            className="mt-1.5"
                        />
                        <Button
                            data-testid="wallet-create-button"
                            onClick={createOwn}
                            disabled={creating || label.trim().length < 2}
                            className="mt-3 w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                        >
                            {creating ? "Creating…" : "Create wallet"}
                        </Button>
                    </Card>
                    <Card className="bg-card border-border p-5">
                        <h2 className="font-heading font-semibold mb-1">Use a seeded sponsor wallet</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            Demo mode: bid with a sample brand budget from the seed data.
                        </p>
                        <Select value={sponsorId} onValueChange={setSponsorId}>
                            <SelectTrigger data-testid="wallet-sponsor-select">
                                <SelectValue placeholder="Pick a demo sponsor" />
                            </SelectTrigger>
                            <SelectContent>
                                {(sponsors.data?.sponsors || []).map((s) => (
                                    <SelectItem key={s.sponsor_id} value={s.sponsor_id}>
                                        {s.brand} — {money(s.budget_usd)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            data-testid="wallet-demo-button"
                            onClick={useSponsor}
                            disabled={creating || sponsorId === NONE}
                            variant="outline"
                            className="mt-3 w-full font-semibold"
                        >
                            {creating ? "Opening…" : "Open demo sponsor wallet"}
                        </Button>
                    </Card>
                </div>
            </div>
        );
    }

    const w = snap.data?.wallet;

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <SampleBanner className="mb-6" />
            <div className="flex items-center justify-between gap-3 mb-2">
                <h1 className="font-heading text-3xl font-bold tracking-tight inline-flex items-center gap-3">
                    <WalletIcon className="h-7 w-7 text-brand" /> Wallet
                </h1>
                <div className="flex items-center gap-2">
                    {w?.is_demo ? <Badge variant="secondary">Demo sponsor</Badge> : null}
                    <Button variant="outline" size="sm" onClick={forget} className="font-semibold" data-testid="wallet-forget">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Forget
                    </Button>
                </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
                {w ? w.label : wallet.label} · {wallet.id.slice(0, 8)}… · bids hold, they don't charge
            </p>

            {snap.isLoading ? (
                <div className="grid grid-cols-3 gap-3 mb-6">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
                </div>
            ) : snap.isError || !w ? (
                <Card className="p-6 border-heat/40 bg-heat/10">
                    <p className="text-sm text-heat">
                        This wallet can't be opened on the server (token mismatch or removed). Forget it
                        and open another one.
                    </p>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-3 mb-6" data-testid="wallet-totals">
                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Balance</div>
                            <div className="font-heading text-xl font-bold tabular">{money(w.balance_usd)}</div>
                        </div>
                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
                                <Lock className="h-3 w-3" /> Held
                            </div>
                            <div className="font-heading text-xl font-bold tabular text-heat">{money(w.held_usd)}</div>
                        </div>
                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Available</div>
                            <div className="font-heading text-xl font-bold tabular text-brand">{money(w.available_usd)}</div>
                        </div>
                    </div>

                    <Card className="bg-card border-border p-5 mb-6" data-testid="wallet-topup">
                        <h2 className="font-heading font-semibold mb-1 inline-flex items-center gap-2">
                            <ArrowDownUp className="h-4 w-4 text-brand" /> Top up
                        </h2>
                        <p className="text-xs text-muted-foreground mb-3">
                            Feedback build — top-ups add demo credits. No payment provider is wired yet.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {PRESETS.map((p) => (
                                <Button
                                    key={p}
                                    variant="outline"
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => topUp(p)}
                                    className="font-semibold tabular"
                                >
                                    +{money(p)}
                                </Button>
                            ))}
                            <div className="flex gap-2 ml-auto">
                                <Input
                                    type="number"
                                    min={1}
                                    data-testid="wallet-custom-topup"
                                    value={custom}
                                    onChange={(e) => setCustom(e.target.value)}
                                    placeholder="Custom"
                                    className="w-28"
                                />
                                <Button
                                    size="sm"
                                    disabled={busy || !Number(custom)}
                                    onClick={() => { topUp(custom); setCustom(""); }}
                                    className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <div className="grid md:grid-cols-2 gap-6">
                        <Card className="bg-card border-border p-5" data-testid="wallet-holds">
                            <h2 className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
                                <Lock className="h-4 w-4 text-heat" /> Active holds
                            </h2>
                            {w.holds.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No holds. Place a bid to hold funds.</p>
                            ) : (
                                <div className="divide-y divide-border">
                                    {w.holds.map((h) => (
                                        <div key={h.listing_id} className="py-2.5 text-sm">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-medium truncate">{h.title}</span>
                                                <span className="tabular font-semibold shrink-0">{money(h.amount_usd)}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                ends in {timeLeft(Math.max(0, Math.round((new Date(h.ends_at).getTime() - Date.now()) / 1000)))}
                                                {" · "}released if outbid
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card className="bg-card border-border p-5" data-testid="wallet-ledger">
                            <h2 className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
                                <Landmark className="h-4 w-4 text-brand" /> Ledger
                            </h2>
                            {w.entries.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No transactions yet.</p>
                            ) : (
                                <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                                    {w.entries.map((e) => {
                                        const meta = KIND_META[e.kind] || { label: e.kind, tone: "" };
                                        return (
                                            <div key={e.id} className="flex items-center justify-between gap-2 text-sm">
                                                <div className="min-w-0">
                                                    <div className={`font-medium ${meta.tone}`}>{meta.label}</div>
                                                    <div className="text-[11px] text-muted-foreground truncate">{e.note || e.ts.slice(0, 10)}</div>
                                                </div>
                                                <span className="tabular font-semibold shrink-0">{money(e.amount_usd)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}



